import React, { useState } from "react";
import { Text, BlockStack, Button, Modal, Badge } from "@shopify/polaris";

const WinnerTable = ({ products, onAnnounce }) => {
  const [activeUser, setActiveUser] = useState(null);
  const groupedData = {};
  products.forEach((item) => {
    if (!groupedData[item.date]) groupedData[item.date] = {};
    if (!groupedData[item.date][item.state])
      groupedData[item.date][item.state] = [];
    groupedData[item.date][item.state].push(item);
  });
  const stateWinnerMap = {};
  products.forEach((item) => {
    const key = `${item.date}-${item.state}-${item.prize}`;
    if (!stateWinnerMap[key]) stateWinnerMap[key] = false;
    if (item.alreadyWinner) stateWinnerMap[key] = true;
  });
  const rowList = [];

  Object.keys(groupedData).forEach((date) => {
    const states = groupedData[date];
    const stateKeys = Object.keys(states);
    const dateRowspan = stateKeys.reduce((sum, s) => sum + states[s].length, 0);
    let isFirstDate = true;
    stateKeys.forEach((state) => {
      const users = states[state];
      const stateRowspan = users.length;
      let isFirstState = true;
      users.forEach((item) => {
        rowList.push({
          item,
          showDate: isFirstDate,
          dateRowspan,
          showState: isFirstState,
          stateRowspan,
          stateKey: `${date}-${state}-${item.prize}`, // per prize key
        });
        isFirstDate = false;
        isFirstState = false;
      });
    });
  });
  const tableStyle = {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "14px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    thStyle = {
      padding: "12px 16px",
      textAlign: "left",
      fontWeight: "600",
      color: "#6d7175",
      borderBottom: "2px solid #e1e3e5",
      backgroundColor: "#f6f6f7",
      fontSize: "13px",
    },
    tdBase = {
      padding: "10px 16px",
      borderBottom: "1px solid #e1e3e5",
      verticalAlign: "middle",
      color: "#202223",
    },
    dateTdStyle = {
      ...tdBase,
      fontWeight: "600",
      fontSize: "15px",
      borderRight: "1px solid #e1e3e5",
    },
    stateTdStyle = {
      ...tdBase,
      fontWeight: "700",
      fontSize: "15px",
      textAlign: "center",
      borderRight: "1px solid #e1e3e5",
    };

  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {[
                "Date",
                "State",
                "User",
                "Phone",
                "Prize",
                "User Details",
                "Status",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    ...thStyle,
                    textAlign: h === "State" ? "center" : "left",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowList.map(
              (
                {
                  item,
                  showDate,
                  dateRowspan,
                  showState,
                  stateRowspan,
                  stateKey,
                },
                idx,
              ) => (
                <tr key={idx}>
                  {showDate && (
                    <td rowSpan={dateRowspan} style={dateTdStyle}>
                      {item.date}
                    </td>
                  )}
                  {showState && (
                    <td rowSpan={stateRowspan} style={stateTdStyle}>
                      {item.state}
                    </td>
                  )}
                  <td style={tdBase}>{item.name || "--"}</td>
                  <td style={tdBase}>{item.phone || "--"}</td>
                  <td style={tdBase}>{item.prize || "--"}</td>
                  <td style={tdBase}>
                    {item.empty ? (
                      "--"
                    ) : (
                      <Button
                        variant="plain"
                        onClick={() => setActiveUser(item)}
                      >
                        View details
                      </Button>
                    )}
                  </td>
                  <td style={tdBase}>
                    {item.empty ? (
                      "--"
                    ) : stateWinnerMap[stateKey] ? (
                      item.alreadyWinner ? (
                        <Badge tone="success">Winner</Badge>
                      ) : (
                        <Badge tone="attention">Not</Badge>
                      )
                    ) : (
                      <Button
                        size="slim"
                        variant="primary"
                        onClick={() => onAnnounce(item)}
                      >
                        Announce
                      </Button>
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={!!activeUser}
        onClose={() => setActiveUser(null)}
        title="User Details"
        primaryAction={{
          content: "Close",
          onAction: () => setActiveUser(null),
        }}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text>
              <b>Name:</b> {activeUser?.name}
            </Text>
            <Text>
              <b>Email:</b> {activeUser?.email}
            </Text>
            <Text>
              <b>Phone:</b> {activeUser?.phone}
            </Text>
            <Text>
              <b>State:</b> {activeUser?.state}
            </Text>
            <Text>
              <b>Prize:</b> {activeUser?.prize}
            </Text>
            <Text>
              <b>Total Amount:</b> ₹{activeUser?.totalAmount}
            </Text>
            <Text>
              <b>Status:</b>
              {activeUser?.alreadyWinner
                ? "Winner Already Announced"
                : "Winner"}
            </Text>
            <Text>
              <b>Eligible:</b> {activeUser?.eligible ? "Yes" : "No"}
            </Text>
            <Text>
              <b>Date:</b>
              {new Date(activeUser?.createdAt).toLocaleString()}
            </Text>
            {activeUser?.attachment && (
              <Button
                variant="plain"
                onClick={() => window.open(activeUser.attachment, "_blank")}
              >
                View Attachment
              </Button>
            )}
            <Text variant="headingMd">Invoiced Products</Text>
            {activeUser?.invoicedProducts?.length > 0 ? (
              activeUser.invoicedProducts.map((prod, i) => (
                <BlockStack key={i} gap="100">
                  <Text>
                    <b>SKU:</b> {prod.sku}
                  </Text>
                  <Text>
                    <b>Qty:</b> {prod.quantity}
                  </Text>
                  <Text>
                    <b>Total:</b> ₹{prod.total_price}
                  </Text>
                  <hr />
                </BlockStack>
              ))
            ) : (
              <Text>No invoiced products</Text>
            )}
            <Text variant="headingMd">Eligible Products</Text>
            {activeUser?.eligibleProducts?.length > 0 ? (
              activeUser.eligibleProducts.map((prod, i) => (
                <BlockStack key={i} gap="100">
                  <Text>
                    <b>Title:</b> {prod.title}
                  </Text>
                  <Text>
                    <b>SKU:</b> {prod.sku}
                  </Text>
                  <Text>
                    <b>Category:</b> {prod.category}
                  </Text>
                  <Text>
                    <b>Qty:</b> {prod.quantity}
                  </Text>
                  <Text>
                    <b>Price:</b> ₹{prod.total_price}
                  </Text>
                  <hr />
                </BlockStack>
              ))
            ) : (
              <Text>No eligible products</Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default WinnerTable;
